import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner, Table } from "reactstrap";

import api from "../../../services/api";
import CteAuditDetailModal from "../../../components/cte/CteAuditDetailModal";
import PageHero, { PageHeroExportButton } from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import MetricCard from "../../../components/MetricCard";
import MetricCardGroup from "../../../components/MetricCardGroup";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import SortHeader from "../../../components/SortHeader";
import TablePagination from "../../../components/TablePagination";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import { useCompanies } from "../../../hooks/companies";
import { useToast } from "../../../hooks/toast";
import useDebounce from "../../../hooks/useDebounce";
import { formatCurrency } from "../../../utils/formatCurrency";
import { rotuloDocumento } from "../../../utils/modeloDocumento";
import { exportCsv } from "../../../utils/exportCsv";
import { paginate, sortRows, toggleSort } from "../../../utils/tableHelpers";
import LogModal from "./LogModal";
import {
  ALL_STATUS_CALCULO,
  ASYNC_CTE_THRESHOLD,
  ASYNC_STATUS_FINAIS,
  DATE_SHORTCUTS,
  DEFAULT_STATUS_CALCULO,
  SITUACAO_CTE_OPTIONS,
  STATUS_CALCULO_OPTIONS,
  TIPO_ARQUIVO,
  TIPO_CTE,
  TIPO_NF,
  buildRecalculoRequest,
  formatDateTimeLabel,
  formatPresetPeriodLabel,
  mapearResultados,
  rotuloStatusAsync,
  totaisDosResultados,
} from "./regras";
import "./recalcular.css";

// Recalcular frete no padrão do Hub (sprint 638): PageHero, escolha do tipo de
// execução, FilterBand com os filtros do recorte, estimativa em MetricCard,
// confirmação antes de gravar, acompanhamento da fila assíncrona e resultado
// em ShellCard com ordenação, paginação e exportação. Regras, endpoints e corpo
// das requisições não mudaram (ver ./regras.js, copiado da versão anterior).

const TIPOS_EXECUCAO = [
  {
    value: TIPO_NF,
    title: "Nota Fiscal (NFe)",
    description: "Use filtros por nota, destinatário, remetente e negócio.",
    iconClassName: "tim-icons icon-paper",
    tone: "info",
  },
  {
    value: TIPO_CTE,
    title: "Conhecimento de Transporte (CTe)",
    description: "Use o período e a situação do CTe para localizar divergências.",
    iconClassName: "tim-icons icon-delivery-fast",
    tone: "success",
  },
  {
    value: TIPO_ARQUIVO,
    title: "Importar Arquivo",
    description: "Envie um arquivo .txt ou .csv com chaves de NFe (44 dígitos cada).",
    iconClassName: "tim-icons icon-cloud-upload-94",
    tone: "pink",
  },
];

const STATUS_TONE = { Calculado: "success", Pendente: "warning", Erro: "danger" };

function NumerosNFsCell({ cell }) {
  if (!cell || cell === "-") return "-";
  const nums = cell.split(", ");
  const MAX = 3;
  if (nums.length <= MAX) return <span>{cell}</span>;
  return (
    <span title={cell} style={{ cursor: "help" }}>
      {nums.slice(0, MAX).join(", ")} +{nums.length - MAX}
    </span>
  );
}

function DateShortcutBar({ activeShortcut, onApply }) {
  return (
    <div className="recalc-atalhos">
      {DATE_SHORTCUTS.map((shortcut) => {
        const isActive = activeShortcut === shortcut.label;
        return (
          <button
            key={shortcut.label}
            type="button"
            className={`recalc-atalho${isActive ? " is-active" : ""}`}
            aria-pressed={isActive}
            onClick={() => onApply(shortcut)}
          >
            {shortcut.label}
          </button>
        );
      })}
    </div>
  );
}

function Grupo({ title, hint, children }) {
  return (
    <div className="recalc-grupo">
      <div className="recalc-grupo__titulo">{title}</div>
      {hint ? <div className="recalc-grupo__dica">{hint}</div> : null}
      {children}
    </div>
  );
}

function Checks({ options, selected, onToggle }) {
  return (
    <div className="recalc-checks">
      {options.map((opt) => (
        <label key={opt.value} className="recalc-check">
          <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => onToggle(opt.value)} />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function ConfirmarRecalculo({ isOpen, titulo, children, rotulo, onConfirm, onCancel }) {
  return (
    <Modal isOpen={isOpen} toggle={onCancel} modalClassName="modal-align-top" fade={false}>
      <ModalHeader toggle={onCancel} tag="h5">
        {titulo}
      </ModalHeader>
      <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
        <div style={{ marginBottom: "1.25rem" }}>{children}</div>
        <div className="recalc-modal-rodape">
          <Button color="default" outline size="sm" onClick={onCancel}>
            Cancelar
          </Button>
          <Button color="info" size="sm" onClick={onConfirm} data-testid="confirmar-recalculo">
            {rotulo}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}

const Recalcular = () => {
  const location = useLocation();
  const { addToast } = useToast();
  const { empresa, transportadora, hasEmpresaSelection, optionsTransportadora, selectTransportadora } = useCompanies();
  const empresaId = empresa?.value;
  const transportadoraId = transportadora?.value;

  const [tipo, setTipo] = useState(TIPO_NF);
  const [loading, setLoading] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [totais, setTotais] = useState(null);
  const [logRow, setLogRow] = useState(null);
  const [cteRow, setCteRow] = useState(null);
  const [cteDetails, setCteDetails] = useState(null);
  const [cteLoading, setCteLoading] = useState(false);
  const [cteError, setCteError] = useState("");
  const [activeShortcut, setActiveShortcut] = useState(null);
  const [activeResumoFiltro, setActiveResumoFiltro] = useState(null);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewCountLoading, setPreviewCountLoading] = useState(false);
  const [previewCountError, setPreviewCountError] = useState("");
  const [previewDiagnostico, setPreviewDiagnostico] = useState(null);
  const [executionError, setExecutionError] = useState("");
  const [asyncExecution, setAsyncExecution] = useState(null);
  const [presetContext, setPresetContext] = useState(() => (location.state && location.state.recalculoPreset) || null);
  const [presetApplied, setPresetApplied] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  // Tabela do resultado (client-side)
  const [busca, setBusca] = useState("");
  const [sort, setSort] = useState({ field: "idx", dir: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Importar arquivo
  const [arquivoChaves, setArquivoChaves] = useState([]);
  const [arquivoNome, setArquivoNome] = useState("");
  const [arquivoErro, setArquivoErro] = useState("");

  // Filtros NF
  const [statusCalculo, setStatusCalculo] = useState(DEFAULT_STATUS_CALCULO);
  const [dataEmissaoNFeDe, setDataEmissaoNFeDe] = useState("");
  const [dataEmissaoNFeAte, setDataEmissaoNFeAte] = useState("");
  const [chaveNFe, setChaveNFe] = useState("");
  const [cnpjDestinatario, setCnpjDestinatario] = useState("");
  const [idEmbarcadorRemetente, setIdEmbarcadorRemetente] = useState("");
  const [idNegocio, setIdNegocio] = useState("");
  const [idEstadoDestino, setIdEstadoDestino] = useState("");
  const [idCidadeDestino, setIdCidadeDestino] = useState("");
  const [remetentes, setRemetentes] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [estadosDestino, setEstadosDestino] = useState([]);
  const [cidadesDestino, setCidadesDestino] = useState([]);

  // Filtros CTe
  const [situacoes, setSituacoes] = useState([1, 3]);
  const [somentePendentesCalculo, setSomentePendentesCalculo] = useState(false);
  const [dataEmissaoCTeDe, setDataEmissaoCTeDe] = useState("");
  const [dataEmissaoCTeAte, setDataEmissaoCTeAte] = useState("");
  const [numeroCte, setNumeroCte] = useState("");
  const [chaveAcessoCte, setChaveAcessoCte] = useState("");

  useEffect(() => {
    if (!empresaId) {
      setNegocios([]);
      setRemetentes([]);
      setResultados([]);
      setTotais(null);
      setLogRow(null);
      setCteRow(null);
      setCteDetails(null);
      setCteError("");
      setActiveResumoFiltro(null);
      setPreviewCount(null);
      setPreviewCountLoading(false);
      setPreviewCountError("");
      setPreviewDiagnostico(null);
      setExecutionError("");
      setAsyncExecution(null);
      return;
    }
    api.get(`/embarcadores/${empresaId}/negocios`).then((res) => {
      setNegocios(Array.isArray(res.data) ? res.data : []);
    }).catch(() => setNegocios([]));

    api.get(`/embarcadores/${empresaId}`).then((res) => {
      const seen = new Set();
      const opts = Array.isArray(res.data)
        ? res.data.reduce((acc, item) => {
            const value = item.embarcador_id || item.matriz_id || item.matrizId;
            if (value && !seen.has(value)) {
              seen.add(value);
              acc.push({ value, label: item.embarcador_fantasia });
            }
            return acc;
          }, [])
        : [];
      setRemetentes(opts);
    }).catch(() => setRemetentes([]));
  }, [empresaId]);

  useEffect(() => {
    if (!presetContext || presetApplied) return;

    setTipo(TIPO_NF);
    setStatusCalculo(ALL_STATUS_CALCULO);
    setDataEmissaoNFeDe(presetContext.periodoSugerido?.de || "");
    setDataEmissaoNFeAte(presetContext.periodoSugerido?.ate || "");
    setChaveNFe("");
    setCnpjDestinatario("");
    setIdEmbarcadorRemetente("");
    setIdNegocio("");
    setIdEstadoDestino("");
    setIdCidadeDestino("");
    setArquivoChaves([]);
    setArquivoNome("");
    setArquivoErro("");
    setSituacoes([1, 3]);
    setSomentePendentesCalculo(false);
    setActiveShortcut(null);
    limparResultados();
    setPresetApplied(true);
  }, [presetApplied, presetContext]);

  useEffect(() => {
    if (!presetContext?.transportadoraId || !Array.isArray(optionsTransportadora) || !optionsTransportadora.length) {
      return;
    }

    const currentTransportadoraId = transportadora?.value;
    if (String(currentTransportadoraId) === String(presetContext.transportadoraId)) {
      return;
    }

    const match = optionsTransportadora.find(
      (option) => String(option.value) === String(presetContext.transportadoraId)
    );

    if (match) {
      selectTransportadora(match);
    }
  }, [optionsTransportadora, presetContext, selectTransportadora, transportadora]);

  useEffect(() => {
    let active = true;

    api.get("/frete/estados")
      .then((response) => {
        if (!active) return;
        setEstadosDestino(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (!active) return;
        setEstadosDestino([]);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    if (!idEstadoDestino) {
      setCidadesDestino([]);
      setIdCidadeDestino("");
      return () => {
        active = false;
      };
    }

    api.get(`/frete/estados/${idEstadoDestino}/cidades`)
      .then((response) => {
        if (!active) return;
        const cidades = Array.isArray(response.data) ? response.data : [];
        setCidadesDestino(cidades);
        setIdCidadeDestino((current) => (
          cidades.some((item) => String(item.id) === String(current)) ? current : ""
        ));
      })
      .catch(() => {
        if (!active) return;
        setCidadesDestino([]);
        setIdCidadeDestino("");
      });

    return () => {
      active = false;
    };
  }, [idEstadoDestino]);

  const filtrosRequisicao = {
    tipo,
    empresaId,
    transportadoraId,
    arquivoChaves,
    statusCalculo,
    dataEmissaoNFeDe,
    dataEmissaoNFeAte,
    chaveNFe,
    cnpjDestinatario,
    idEmbarcadorRemetente,
    idNegocio,
    idEstadoDestino,
    idCidadeDestino,
    situacoes,
    somentePendentesCalculo,
    dataEmissaoCTeDe,
    dataEmissaoCTeAte,
    numeroCte,
    chaveAcessoCte,
  };

  const previewRequest = useMemo(
    () => buildRecalculoRequest(filtrosRequisicao),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      arquivoChaves,
      cnpjDestinatario,
      dataEmissaoCTeAte,
      dataEmissaoCTeDe,
      dataEmissaoNFeAte,
      dataEmissaoNFeDe,
      empresaId,
      idCidadeDestino,
      idEmbarcadorRemetente,
      idEstadoDestino,
      idNegocio,
      chaveNFe,
      somentePendentesCalculo,
      situacoes,
      statusCalculo,
      tipo,
      transportadoraId,
      numeroCte,
      chaveAcessoCte,
    ]
  );

  const debouncedPreviewRequest = useDebounce(previewRequest, 350);

  useEffect(() => {
    if (!debouncedPreviewRequest || !debouncedPreviewRequest.canExecute) {
      setPreviewCount(null);
      setPreviewCountLoading(false);
      setPreviewCountError("");
      setPreviewDiagnostico(null);
      return;
    }

    let active = true;
    const cancelSource = axios.CancelToken.source();

    setPreviewCountLoading(true);
    setPreviewCountError("");

    api
      .post(debouncedPreviewRequest.previewEndpoint, debouncedPreviewRequest.body, {
        cancelToken: cancelSource.token,
      })
      .then((response) => {
        if (!active) return;

        const total = response?.data?.total;
        const parsed = total == null ? 0 : Number(total);
        const count = Number.isNaN(parsed) ? 0 : parsed;
        setPreviewCount(count);
        setPreviewDiagnostico(count === 0 ? response?.data?.diagnostico || null : null);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        if (!active) return;

        setPreviewCount(null);
        setPreviewDiagnostico(null);
        setPreviewCountError(
          error?.response?.data?.error || "Nao foi possivel estimar a quantidade com os filtros atuais."
        );
      })
      .finally(() => {
        if (active) {
          setPreviewCountLoading(false);
        }
      });

    return () => {
      active = false;
      cancelSource.cancel("preview request superseded");
    };
  }, [debouncedPreviewRequest]);

  const asyncExecutionId = asyncExecution?.executionId || null;
  const asyncExecutionStatus = asyncExecution?.status || null;

  useEffect(() => {
    if (!asyncExecutionId) return undefined;
    if (ASYNC_STATUS_FINAIS.includes(asyncExecutionStatus)) {
      return undefined;
    }

    let active = true;

    const pollExecution = async () => {
      try {
        const response = await api.get(`/recalculo/cte/executions/${asyncExecutionId}`, {
          params: { embarcadorId: empresaId },
        });

        if (!active) return;
        setAsyncExecution(response.data || null);
      } catch (error) {
        if (!active) return;
        setExecutionError(
          error?.response?.data?.error || "Nao foi possivel atualizar o andamento do recálculo assíncrono."
        );
      }
    };

    pollExecution();
    const intervalId = window.setInterval(pollExecution, 5000);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [asyncExecutionId, asyncExecutionStatus, empresaId]);

  function toggleStatus(value) {
    setStatusCalculo((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  }

  function toggleSituacao(value) {
    setSituacoes((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  }

  function handleArquivo(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setArquivoNome(file.name);
    setArquivoErro("");
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result || "";
      const chaves = text
        .split(/[\r\n,;]+/)
        .map((s) => s.trim().replace(/\D/g, ""))
        .filter((s) => s.length === 44);
      if (chaves.length === 0) {
        setArquivoErro("Nenhuma chave válida encontrada. Cada chave deve ter 44 dígitos.");
      }
      setArquivoChaves(chaves);
    };
    reader.readAsText(file);
  }

  function aplicarAtalho(shortcut) {
    const { de, ate } = shortcut.getDates();
    setActiveShortcut(shortcut.label);
    if (tipo === TIPO_NF) {
      setDataEmissaoNFeDe(de);
      setDataEmissaoNFeAte(ate);
    } else {
      setDataEmissaoCTeDe(de);
      setDataEmissaoCTeAte(ate);
    }
  }

  function limparResultados() {
    setResultados([]);
    setTotais(null);
    setExecutionError("");
    setAsyncExecution(null);
    setLogRow(null);
    setCteRow(null);
    setCteDetails(null);
    setCteError("");
    setActiveResumoFiltro(null);
    setBusca("");
    setPage(1);
  }

  function handleTipoChange(nextTipo) {
    if (nextTipo === tipo) return;
    setTipo(nextTipo);
    setActiveShortcut(null);
    limparResultados();
    if (nextTipo !== TIPO_ARQUIVO) {
      setArquivoChaves([]);
      setArquivoNome("");
      setArquivoErro("");
    }
  }

  function limpar() {
    limparResultados();
    setActiveShortcut(null);
    if (tipo === TIPO_ARQUIVO) {
      setArquivoChaves([]);
      setArquivoNome("");
      setArquivoErro("");
      return;
    }
    if (tipo === TIPO_NF) {
      setStatusCalculo(DEFAULT_STATUS_CALCULO);
      setDataEmissaoNFeDe("");
      setDataEmissaoNFeAte("");
      setChaveNFe("");
      setCnpjDestinatario("");
      setIdEmbarcadorRemetente("");
      setIdNegocio("");
      setIdEstadoDestino("");
      setIdCidadeDestino("");
      return;
    }

    setSituacoes([1, 3]);
    setSomentePendentesCalculo(false);
    setDataEmissaoCTeDe("");
    setDataEmissaoCTeAte("");
    setNumeroCte("");
    setChaveAcessoCte("");
  }

  async function calcular() {
    const request = buildRecalculoRequest(filtrosRequisicao);

    if (!request || !request.canExecute) return;

    const shouldUseAsyncCte =
      tipo === TIPO_CTE &&
      previewCount != null &&
      Number(previewCount) > ASYNC_CTE_THRESHOLD;
    const endpoint = shouldUseAsyncCte ? "/recalculo/cte/async" : request.endpoint;

    setLoading(true);
    limparResultados();

    try {
      const response = await api.post(endpoint, request.body);

      if (shouldUseAsyncCte) {
        setAsyncExecution(response.data || null);
        addToast({
          place: "tr",
          type: "info",
          title: "Recálculo enfileirado",
          description: "Acompanhe o andamento no bloco Execução em segundo plano.",
          icon: "tim-icons icon-time-alarm",
        });
        return;
      }

      if (response.status === 200) {
        const rows = mapearResultados(response.data.resultados, formatCurrency);
        const novosTotais = totaisDosResultados(rows, response.data.total);
        setResultados(rows);
        setTotais(novosTotais);
        addToast({
          place: "tr",
          type: novosTotais.comErro > 0 ? "warning" : "success",
          title: "Recálculo concluído",
          description: `${novosTotais.calculados} calculado(s), ${novosTotais.pendentes} pendente(s), ${novosTotais.comErro} com erro.`,
          icon: "tim-icons icon-check-2",
        });
      }
    } catch (err) {
      console.error("Erro ao recalcular:", err);
      const mensagem = err?.response?.data?.error || "Nao foi possivel concluir o recalculo com os filtros atuais.";
      setExecutionError(mensagem);
      addToast({
        place: "tr",
        type: "danger",
        title: "Recálculo interrompido",
        description: mensagem,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleOpenCte(row) {
    if (!row?.idCTeValue || !empresaId) return;

    setCteRow(row);
    setCteDetails(null);
    setCteError("");
    setCteLoading(true);

    try {
      const response = await api.get(`/dashboard/auditoria/cte/details/${row.idCTeValue}`, {
        params: {
          embarcadorId: empresaId,
        },
      });

      setCteDetails(response.data || null);
    } catch (err) {
      setCteError(err.response?.data?.error || err.message || "Não foi possível carregar os dados do CTe.");
    } finally {
      setCteLoading(false);
    }
  }

  // PO1-3197: recarrega o detalhe depois do cálculo feito no próprio modal, sem
  // mostrar o carregando (o botão e a mensagem do resultado ficam na tela).
  async function handleRecalculatedCte() {
    if (!cteRow?.idCTeValue || !empresaId) return;
    try {
      const response = await api.get(`/dashboard/auditoria/cte/details/${cteRow.idCTeValue}`, {
        params: { embarcadorId: empresaId },
      });
      setCteDetails(response.data || null);
    } catch (err) {
      setCteError(err.response?.data?.error || err.message || "Não foi possível recarregar os dados do CTe.");
    }
  }

  function handleCloseCte() {
    setCteRow(null);
    setCteDetails(null);
    setCteError("");
    setCteLoading(false);
  }

  function handleResumoFiltro(filter) {
    setPage(1);
    if (!filter) {
      setActiveResumoFiltro(null);
      return;
    }
    setActiveResumoFiltro((current) => (current === filter ? null : filter));
  }

  // Busca do FilterBand: no NF é a chave da NFe; no CTe, número (até 12 dígitos)
  // ou chave de acesso (mais que isso), os mesmos dois parâmetros de antes.
  const buscaCte = chaveAcessoCte || numeroCte;
  function handleBuscaCte(valor) {
    const digitos = String(valor || "").replace(/\D/g, "").slice(0, 44);
    if (digitos.length > 12) {
      setChaveAcessoCte(digitos);
      setNumeroCte("");
    } else {
      setNumeroCte(digitos);
      setChaveAcessoCte("");
    }
  }

  const isCte = tipo === TIPO_CTE;
  const isAsyncCteEligible = isCte && previewCount != null && Number(previewCount) > ASYNC_CTE_THRESHOLD;
  const asyncExecutionCompleted = ASYNC_STATUS_FINAIS.includes(asyncExecution?.status);
  const asyncProgressPercent =
    asyncExecution?.total > 0
      ? Math.min(100, Math.round(((asyncExecution?.processed || 0) / asyncExecution.total) * 100))
      : 100;
  const podeRecalcular = !loading && Boolean(empresa?.value) && !(tipo === TIPO_ARQUIVO && arquivoChaves.length === 0);

  const filtrosNfAtivos =
    (dataEmissaoNFeDe || dataEmissaoNFeAte ? 1 : 0) +
    (cnpjDestinatario ? 1 : 0) +
    (idEstadoDestino ? 1 : 0) +
    (idCidadeDestino ? 1 : 0) +
    (idEmbarcadorRemetente ? 1 : 0) +
    (idNegocio ? 1 : 0) +
    (String(statusCalculo.slice().sort()) !== String(DEFAULT_STATUS_CALCULO.slice().sort()) ? 1 : 0);
  const filtrosCteAtivos =
    (dataEmissaoCTeDe || dataEmissaoCTeAte ? 1 : 0) +
    (String(situacoes.slice().sort()) !== "1,3" ? 1 : 0) +
    (somentePendentesCalculo ? 1 : 0);

  // Resultado: filtro pelos cards, busca, ordenação e paginação client-side.
  const resultadosFiltrados = useMemo(() => {
    const porStatus = activeResumoFiltro ? resultados.filter((item) => item.status === activeResumoFiltro) : resultados;
    const termo = busca.trim().toLowerCase();
    if (!termo) return porStatus;
    return porStatus.filter((item) =>
      [item.transportadora, item.numeroCTe, item.numerosNFs, item.valorSimulado, item.valorCobrado, item.diferenca, item.status]
        .some((v) => String(v || "").toLowerCase().includes(termo))
    );
  }, [activeResumoFiltro, busca, resultados]);
  const ordenados = useMemo(
    () =>
      sortRows(resultadosFiltrados, sort, (row, field) => {
        if (field === "valorSimulado") return row.valorSimuladoNum;
        if (field === "valorCobrado") return row.valorCobradoNum;
        if (field === "diferenca") return row.diferencaNum;
        return row[field];
      }),
    [resultadosFiltrados, sort]
  );
  const paged = paginate(ordenados, page, pageSize);
  const handleSort = (field) => {
    setSort((prev) => toggleSort(prev, field));
    setPage(1);
  };

  function exportarResultado() {
    exportCsv({
      filename: `recalculo-${tipo.toLowerCase()}`,
      columns: [
        { key: "idx", label: "#" },
        { key: "transportadora", label: "Transportadora" },
        isCte ? { key: "numeroCTe", label: "Documento" } : { key: "numerosNFs", label: "Notas Fiscais" },
        { key: "valorSimulado", label: "Valor Simulado" },
        { key: "valorCobrado", label: "Valor Cobrado" },
        { key: "diferenca", label: "Diferença" },
        { key: "status", label: "Status" },
        { key: "erro", label: "Erro" },
      ],
      rows: ordenados,
    });
  }

  if (!hasEmpresaSelection) {
    return (
      <div className="content recalcular-page">
        <SpecificEmpresaRequiredNotice />
      </div>
    );
  }

  const estimativaTexto = previewCountLoading
    ? "Calculando..."
    : previewCountError
      ? "Indisponível"
      : previewCount == null
        ? "—"
        : String(previewCount);
  const estimativaAjuda = previewCountError
    ? previewCountError
    : previewCount == null
      ? tipo === TIPO_ARQUIVO
        ? "Selecione um arquivo para estimar."
        : "Aguardando estimativa..."
      : previewCount === 0
        ? previewDiagnostico && previewDiagnostico.mensagem
          ? `${previewDiagnostico.mensagem} Esse CTe não entra no recálculo pelo motivo acima.`
          : "Nenhum item encontrado com os filtros atuais."
        : isAsyncCteEligible
          ? `Volumes acima de ${ASYNC_CTE_THRESHOLD} CTes entram em fila para processamento assíncrono.`
          : "A quantidade respeita os filtros, a transportadora e o tipo de execução atuais.";
  const rotuloRecalcular = isAsyncCteEligible ? "Enfileirar recálculo" : "Recalcular";

  return (
    <div className="content recalcular-page">
      <LogModal row={logRow} onClose={() => setLogRow(null)} />
      <CteAuditDetailModal
        isOpen={Boolean(cteRow)}
        row={cteRow}
        detail={cteDetails}
        loading={cteLoading}
        error={cteError}
        onClose={handleCloseCte}
        onRecalculated={handleRecalculatedCte}
      />
      <ConfirmarRecalculo
        isOpen={confirmando}
        titulo={isAsyncCteEligible ? "Enfileirar recálculo" : "Confirmar recálculo"}
        rotulo={rotuloRecalcular}
        onCancel={() => setConfirmando(false)}
        onConfirm={() => {
          setConfirmando(false);
          calcular();
        }}
      >
        {previewCount != null && previewCount > 0 ? (
          <>
            O frete de <strong>{previewCount} item(ns)</strong> será recalculado com os filtros atuais
            {transportadora?.label ? (
              <>
                {" "}na transportadora <strong>{transportadora.label}</strong>
              </>
            ) : null}
            . O valor simulado gravado será substituído.
          </>
        ) : (
          <>O frete dos itens do recorte atual será recalculado e o valor simulado gravado será substituído.</>
        )}
        {isAsyncCteEligible ? (
          <div className="recalc-aviso">
            Acima de {ASYNC_CTE_THRESHOLD} CTes o recálculo vai para a fila e roda em segundo plano.
          </div>
        ) : null}
      </ConfirmarRecalculo>

      <PageHero
        eyebrow="Operação"
        title="Recalcular frete"
        description="Escolha o tipo de cálculo, aplique os filtros e recalcule só o que precisa de revisão. A estimativa mostra quantos itens entram antes de gravar."
        actions={
          <>
            <Button color="default" outline className="btn-sm" onClick={limpar} disabled={loading}>
              Limpar
            </Button>
            <Button
              color="info"
              className="btn-sm"
              onClick={() => setConfirmando(true)}
              disabled={!podeRecalcular}
              data-testid="recalcular-hero"
            >
              {loading ? "Processando..." : rotuloRecalcular}
            </Button>
          </>
        }
      >
        {presetContext ? (
          <div className="recalc-preset" data-testid="recalc-preset">
            <div className="recalc-preset__titulo">
              Recálculo iniciado a partir da tabela {presetContext.tabelaNome ? `"${presetContext.tabelaNome}"` : "selecionada"}
            </div>
            <div className="recalc-preset__pills">
              {presetContext.transportadoraNome ? (
                <span className="recalc-pill">
                  <strong>Transportadora:</strong> {presetContext.transportadoraNome}
                </span>
              ) : null}
              {formatPresetPeriodLabel(presetContext.periodoSugerido) ? (
                <span className="recalc-pill">
                  <strong>Período sugerido:</strong> {formatPresetPeriodLabel(presetContext.periodoSugerido)}
                </span>
              ) : null}
              <span className="recalc-pill">
                <strong>Status:</strong> todos
              </span>
              {presetContext.estimativaImpacto != null ? (
                <span className="recalc-pill">
                  <strong>Estimativa inicial:</strong> {presetContext.estimativaImpacto} documento(s)
                </span>
              ) : null}
              <button type="button" className="recalc-preset__remover" onClick={() => setPresetContext(null)}>
                Remover contexto
              </button>
            </div>
          </div>
        ) : null}
      </PageHero>

      <ShellCard
        eyebrow="Escolha inicial"
        title="Tipo de execução"
        description="Escolha primeiro o contexto e depois refine o recorte com os filtros."
      >
        <div className="recalc-tipos" role="group" aria-label="Tipo de execução">
          {TIPOS_EXECUCAO.map((item) => {
            const isActive = tipo === item.value;
            return (
              <button
                key={item.value}
                type="button"
                className={`recalc-tipo recalc-tipo--${item.tone}${isActive ? " is-active" : ""}`}
                onClick={() => handleTipoChange(item.value)}
                aria-pressed={isActive}
                data-tipo={item.value}
              >
                <span className="recalc-tipo__icone" aria-hidden="true">
                  <i className={item.iconClassName} />
                </span>
                <span className="recalc-tipo__texto">
                  <span className="recalc-tipo__titulo">{item.title}</span>
                  <span className="recalc-tipo__descricao">{item.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      </ShellCard>

      {tipo === TIPO_NF ? (
        <FilterBand
          searchLabel="Chave da NFe"
          searchHint="Recalcula só esta nota, quando já houver alvo conhecido"
          searchPlaceholder="Chave de acesso da NFe"
          searchValue={chaveNFe}
          onSearchChange={setChaveNFe}
          activeFiltersCount={filtrosNfAtivos}
          defaultOpen
          panelTitle="Recorte do recálculo por NFe"
          panelDescription="Monte uma fila precisa combinando período, destinatário, destino e escopo operacional."
        >
          <Row>
            <Col lg="6" xl="3">
              <Grupo title="Período" hint="Delimita a emissão local das notas fiscais que entram no recálculo.">
                <DateShortcutBar activeShortcut={activeShortcut} onApply={aplicarAtalho} />
                <div className="recalc-datas">
                  <FormGroup>
                    <Label for="recalc-nf-de">Data emissão de</Label>
                    <Input
                      id="recalc-nf-de"
                      type="date"
                      value={dataEmissaoNFeDe}
                      onChange={(e) => {
                        setDataEmissaoNFeDe(e.target.value);
                        setActiveShortcut(null);
                      }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="recalc-nf-ate">Data emissão até</Label>
                    <Input
                      id="recalc-nf-ate"
                      type="date"
                      value={dataEmissaoNFeAte}
                      onChange={(e) => {
                        setDataEmissaoNFeAte(e.target.value);
                        setActiveShortcut(null);
                      }}
                    />
                  </FormGroup>
                </div>
              </Grupo>
            </Col>
            <Col lg="6" xl="3">
              <Grupo title="Destinatário" hint="Use o documento do destinatário quando o alvo já for conhecido.">
                <FormGroup>
                  <Label for="recalc-cnpj">CNPJ destinatário</Label>
                  <Input
                    id="recalc-cnpj"
                    type="text"
                    value={cnpjDestinatario}
                    onChange={(e) => setCnpjDestinatario(e.target.value)}
                    placeholder="00.000.000/0000-00"
                  />
                </FormGroup>
              </Grupo>
            </Col>
            <Col lg="6" xl="3">
              <Grupo title="Destino" hint="Isola um estado ou uma cidade de entrega.">
                <FormGroup>
                  <Label for="recalc-uf">UF destino</Label>
                  <Input
                    id="recalc-uf"
                    type="select"
                    value={idEstadoDestino}
                    onChange={(e) => {
                      setIdEstadoDestino(e.target.value);
                      setIdCidadeDestino("");
                    }}
                  >
                    <option value="">Todas</option>
                    {estadosDestino.map((estado) => (
                      <option key={estado.id} value={estado.id}>{estado.uf}</option>
                    ))}
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label for="recalc-cidade">Cidade destino</Label>
                  <Input
                    id="recalc-cidade"
                    type="select"
                    value={idCidadeDestino}
                    onChange={(e) => setIdCidadeDestino(e.target.value)}
                    disabled={!idEstadoDestino}
                  >
                    <option value="">Todas</option>
                    {cidadesDestino.map((cidade) => (
                      <option key={cidade.id} value={cidade.id}>{cidade.nome}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Grupo>
            </Col>
            <Col lg="6" xl="3">
              <Grupo title="Escopo operacional" hint="Remetente, negócio e situação do cálculo.">
                <FormGroup>
                  <Label for="recalc-remetente">Embarcador remetente</Label>
                  <Input
                    id="recalc-remetente"
                    type="select"
                    value={idEmbarcadorRemetente}
                    onChange={(e) => setIdEmbarcadorRemetente(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {remetentes.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label for="recalc-negocio">Negócio</Label>
                  <Input
                    id="recalc-negocio"
                    type="select"
                    value={idNegocio}
                    onChange={(e) => setIdNegocio(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {negocios.map((n) => (
                      <option key={n.id} value={n.id}>{n.descricao}</option>
                    ))}
                  </Input>
                </FormGroup>
                <Label>Situação do cálculo</Label>
                <Checks options={STATUS_CALCULO_OPTIONS} selected={statusCalculo} onToggle={toggleStatus} />
              </Grupo>
            </Col>
          </Row>
        </FilterBand>
      ) : null}

      {tipo === TIPO_CTE ? (
        <FilterBand
          searchLabel="CTe específico"
          searchHint="Número ou chave de acesso; quando preenchido, os demais filtros são ignorados"
          searchPlaceholder="Número do CTe (ex.: 608740) ou chave de 44 dígitos"
          searchValue={buscaCte}
          onSearchChange={handleBuscaCte}
          activeFiltersCount={filtrosCteAtivos}
          defaultOpen
          panelTitle="Recorte do recálculo por CTe"
          panelDescription="Use período e situação para encontrar divergências e reprocessar o que precisa de revisão."
        >
          <Row>
            <Col lg="6">
              <Grupo title="Período" hint="Delimita a emissão dos CTes considerados nesta rodada.">
                <DateShortcutBar activeShortcut={activeShortcut} onApply={aplicarAtalho} />
                <div className="recalc-datas">
                  <FormGroup>
                    <Label for="recalc-cte-de">Data emissão de</Label>
                    <Input
                      id="recalc-cte-de"
                      type="date"
                      value={dataEmissaoCTeDe}
                      onChange={(e) => {
                        setDataEmissaoCTeDe(e.target.value);
                        setActiveShortcut(null);
                      }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="recalc-cte-ate">Data emissão até</Label>
                    <Input
                      id="recalc-cte-ate"
                      type="date"
                      value={dataEmissaoCTeAte}
                      onChange={(e) => {
                        setDataEmissaoCTeAte(e.target.value);
                        setActiveShortcut(null);
                      }}
                    />
                  </FormGroup>
                </div>
              </Grupo>
            </Col>
            <Col lg="6">
              <Grupo title="Situação" hint="Status de validação do CTe, o mesmo conceito da auditoria.">
                <Label>Situação do CTe</Label>
                <Checks options={SITUACAO_CTE_OPTIONS} selected={situacoes} onToggle={toggleSituacao} />
                <Label style={{ marginTop: "0.75rem" }}>Complemento da fila</Label>
                <div className="recalc-checks">
                  <label className="recalc-check">
                    <input
                      type="checkbox"
                      checked={somentePendentesCalculo}
                      onChange={(e) => setSomentePendentesCalculo(e.target.checked)}
                    />
                    <span>Somente pendentes de cálculo</span>
                  </label>
                </div>
                <div className="recalc-grupo__dica">
                  Situação 1, ou situação 3 com valor simulado zerado, sem perder os demais filtros ativos.
                </div>
              </Grupo>
            </Col>
          </Row>
        </FilterBand>
      ) : null}

      {tipo === TIPO_ARQUIVO ? (
        <ShellCard
          eyebrow="Entrada em lote"
          title="Importar arquivo de chaves"
          description="Envie um .txt ou .csv com chaves de NFe (44 dígitos cada) para montar a fila e conferir o volume antes de recalcular."
        >
          <button
            type="button"
            className="recalc-dropzone"
            onClick={() => document.getElementById("arquivo-chaves-input").click()}
          >
            <i className="tim-icons icon-cloud-upload-94" aria-hidden="true" />
            <span className="recalc-dropzone__texto">
              {arquivoNome ? <strong>{arquivoNome}</strong> : "Clique para selecionar o arquivo"}
            </span>
            {arquivoErro ? <span className="recalc-dropzone__erro">{arquivoErro}</span> : null}
          </button>
          <input
            id="arquivo-chaves-input"
            data-testid="arquivo-chaves-input"
            type="file"
            accept=".txt,.csv"
            style={{ display: "none" }}
            onChange={handleArquivo}
          />
          <MetricCardGroup style={{ marginTop: "1rem", boxShadow: "none", background: "transparent" }}>
            <MetricCard label="Arquivo" value={arquivoNome || "—"} helper={arquivoNome ? "selecionado" : "aguardando seleção"} />
            <MetricCard label="Chaves válidas" value={arquivoChaves.length} helper="com 44 dígitos" />
            <MetricCard label="Transportadora" value={transportadora?.label || "Todas"} helper="do seletor do topo" />
          </MetricCardGroup>
        </ShellCard>
      ) : null}

      <MetricCardGroup
        eyebrow="Lançamento"
        title="Estimativa e execução"
        description="Confira quantos itens entram antes de recalcular. Volumes altos de CTe vão automaticamente para a fila assíncrona."
      >
        <MetricCard
          label="Itens no recorte"
          value={previewCountLoading ? <Spinner size="sm" /> : estimativaTexto}
          helper={estimativaAjuda}
          iconClassName="tim-icons icon-bullet-list-67"
          tone={previewCountError ? "danger" : previewCount === 0 ? "warning" : "info"}
        />
        <MetricCard
          label="Transportadora"
          value={transportadora?.label || "Todas"}
          helper="escolhida no seletor do topo"
          iconClassName="tim-icons icon-delivery-fast"
          tone="neutral"
        />
        <MetricCard
          label="Modo"
          value={isAsyncCteEligible ? "Fila assíncrona" : "Imediato"}
          helper={isAsyncCteEligible ? "o progresso aparece abaixo" : "o resultado aparece nesta tela"}
          iconClassName="tim-icons icon-time-alarm"
          tone={isAsyncCteEligible ? "warning" : "success"}
        />
        <div className="recalc-lancar">
          <Button
            color="info"
            onClick={() => setConfirmando(true)}
            disabled={!podeRecalcular}
            data-testid="recalcular"
          >
            {loading ? "Processando..." : rotuloRecalcular}
          </Button>
          <Button color="default" outline size="sm" onClick={limpar} disabled={loading}>
            Limpar filtros
          </Button>
        </div>
      </MetricCardGroup>

      {executionError ? (
        <div className="recalc-erro" role="alert" data-testid="recalc-erro">
          <strong>Execução interrompida.</strong> {executionError}
        </div>
      ) : null}

      {asyncExecution ? (
        <ShellCard
          eyebrow="Acompanhamento"
          title="Execução em segundo plano"
          description="Andamento da fila, progresso processado e os últimos erros, sem sair da tela."
        >
          <div className="recalc-metricas">
            <MetricCard
              label="Execução"
              value={<span className="recalc-id">{asyncExecution.executionId || "-"}</span>}
              helper={formatDateTimeLabel(asyncExecution.createdAt) || "Aguardando timestamp"}
            />
            <MetricCard
              label="Status"
              value={rotuloStatusAsync(asyncExecution.status)}
              helper={
                asyncExecution.status === "queued"
                  ? "Job enfileirado — o processamento inicia em instantes"
                  : asyncExecution.latestEventAt
                    ? `Último evento em ${formatDateTimeLabel(asyncExecution.latestEventAt)}`
                    : "Sem eventos processados ainda"
              }
            />
            <MetricCard
              label="Progresso"
              value={`${asyncExecution.processed || 0}/${asyncExecution.total || 0}`}
              helper={`${asyncExecution.pending || 0} item(ns) restante(s)`}
            />
            <MetricCard
              label="Sucesso / Erros"
              value={`${asyncExecution.counts?.success || 0} ok, ${asyncExecution.counts?.businessErrors || 0} pend., ${asyncExecution.counts?.technicalErrors || 0} erro(s)`}
              helper={asyncExecutionCompleted ? "A consulta de logs foi finalizada." : "Atualização automática a cada 5 segundos."}
            />
          </div>
          <div className="recalc-progresso">
            <div
              className={`recalc-progresso__barra recalc-progresso__barra--${
                asyncExecution.status === "completed_with_errors"
                  ? "danger"
                  : asyncExecution.status === "completed_with_business_errors"
                    ? "warning"
                    : "accent"
              }`}
              style={{ width: `${asyncProgressPercent}%` }}
            />
          </div>
          <div className="recalc-grupo__dica">
            {asyncExecutionCompleted
              ? "Os logs desta execução foram correlacionados pelo identificador da fila."
              : "A tela consulta o Loki usando o identificador da execução para atualizar o progresso."}
          </div>
          {asyncExecution.recentErrors?.length ? (
            <div className="recalc-erros-recentes">
              <div className="recalc-grupo__titulo">Últimos logs com erro</div>
              {asyncExecution.recentErrors.map((log, index) => (
                <div key={`${log.timestamp || "log"}-${log.numero || index}`} className="recalc-erro-recente">
                  <div className="recalc-erro-recente__titulo">
                    {log.numero ? `${rotuloDocumento(log.modelo)} ${log.numero}` : "Execução"}
                    {log.statusCode ? ` • status ${log.statusCode}` : ""}
                  </div>
                  <div className="recalc-erro-recente__meta">
                    {formatDateTimeLabel(log.timestamp) || "Sem timestamp"}
                    {log.transportadora ? ` • ${log.transportadora}` : ""}
                  </div>
                  <div>{log.message || "Sem mensagem detalhada."}</div>
                </div>
              ))}
            </div>
          ) : null}
        </ShellCard>
      ) : null}

      {totais ? (
        <MetricCardGroup
          eyebrow="Resultado"
          title="Resumo da execução"
          description={
            activeResumoFiltro
              ? `Exibindo apenas itens com status "${activeResumoFiltro}". Clique de novo no card ou em "Total processado" para ver todos.`
              : "Clique nos cards para filtrar a lista sem alterar o resultado já processado."
          }
        >
          <MetricCard
            label="Total processado"
            value={totais.total}
            helper="itens retornados para o recálculo"
            iconClassName="tim-icons icon-components"
            tone="info"
            onClick={() => handleResumoFiltro(null)}
            active={activeResumoFiltro === null}
          />
          <MetricCard
            label="Calculados"
            value={totais.calculados}
            helper="status calculado"
            iconClassName="tim-icons icon-check-2"
            tone="success"
            onClick={() => handleResumoFiltro("Calculado")}
            active={activeResumoFiltro === "Calculado"}
          />
          <MetricCard
            label="Pendentes"
            value={totais.pendentes}
            helper="aguardam revisão"
            iconClassName="tim-icons icon-time-alarm"
            tone="warning"
            onClick={() => handleResumoFiltro("Pendente")}
            active={activeResumoFiltro === "Pendente"}
          />
          <MetricCard
            label="Com erro"
            value={totais.comErro}
            helper="exigem ação"
            iconClassName="tim-icons icon-alert-circle-exc"
            tone="danger"
            onClick={() => handleResumoFiltro("Erro")}
            active={activeResumoFiltro === "Erro"}
          />
        </MetricCardGroup>
      ) : null}

      {resultados.length > 0 ? (
        <ShellCard
          title="Itens processados"
          description={`${ordenados.length} resultado(s). ${
            isCte
              ? "O número do CTe abre o documento detalhado."
              : "Os detalhes mostram como a busca das tabelas foi montada."
          }`}
          action={
            <div className="recalc-tabela-acoes">
              <Input
                type="search"
                bsSize="sm"
                className="recalc-busca"
                placeholder={isCte ? "Buscar por CTe, valor ou status..." : "Buscar por nota, valor ou status..."}
                value={busca}
                onChange={(e) => {
                  setBusca(e.target.value);
                  setPage(1);
                }}
                aria-label="Buscar nos itens processados"
              />
              <PageHeroExportButton onClick={exportarResultado} disabled={ordenados.length === 0} />
            </div>
          }
        >
          <TableSurface>
            <Table size="sm" responsive hover className="recalc-tabela hub-thead-accent" data-testid="recalc-tabela">
              <colgroup>
                <col style={{ width: 56 }} />
                <col style={{ width: 200 }} />
                <col style={{ width: isCte ? 150 : 230 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 120 }} />
              </colgroup>
              <thead className="text-primary">
                <tr>
                  <th><SortHeader label="#" field="idx" sort={sort} onSort={handleSort} /></th>
                  <th><SortHeader label="Transportadora" field="transportadora" sort={sort} onSort={handleSort} /></th>
                  <th>
                    {isCte ? (
                      <SortHeader label="Documento" field="numeroCTe" sort={sort} onSort={handleSort} />
                    ) : (
                      <SortHeader label="Notas Fiscais" field="numerosNFs" sort={sort} onSort={handleSort} />
                    )}
                  </th>
                  <th className="is-number"><SortHeader label="Valor Simulado" field="valorSimulado" sort={sort} onSort={handleSort} align="right" /></th>
                  <th className="is-number"><SortHeader label="Valor Cobrado" field="valorCobrado" sort={sort} onSort={handleSort} align="right" /></th>
                  <th className="is-number"><SortHeader label="Diferença" field="diferenca" sort={sort} onSort={handleSort} align="right" /></th>
                  <th className="text-center"><SortHeader label="Status" field="status" sort={sort} onSort={handleSort} align="center" /></th>
                  <th className="text-center">Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {paged.items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.idx}</td>
                    <td><strong>{row.transportadora}</strong></td>
                    <td>
                      {isCte ? (
                        row.idCTeValue ? (
                          <button
                            type="button"
                            className="recalc-link"
                            onClick={() => handleOpenCte(row)}
                            title={`Abrir dados do ${rotuloDocumento(row.modelo)}`}
                          >
                            {row.numeroCTe}
                          </button>
                        ) : (
                          row.numeroCTe
                        )
                      ) : (
                        <NumerosNFsCell cell={row.numerosNFs} />
                      )}
                    </td>
                    <td className="is-number">{row.valorSimulado}</td>
                    <td className="is-number">{row.valorCobrado}</td>
                    <td className="is-number">{row.diferenca}</td>
                    <td className="text-center">
                      <span className={`recalc-status recalc-status--${STATUS_TONE[row.status] || "neutral"}`}>{row.status}</span>
                    </td>
                    <td className="text-center">
                      <Button type="button" size="sm" color="info" outline className="recalc-detalhes" onClick={() => setLogRow(row)}>
                        Ver detalhes
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {ordenados.length === 0 ? (
              <div className="recalc-vazio">Nenhum item com a busca ou o status escolhido.</div>
            ) : null}
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              total={paged.total}
              itemLabel="item(ns)"
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </TableSurface>
        </ShellCard>
      ) : null}
    </div>
  );
};

export default Recalcular;
